import { widgetApi } from "../services/api";

/**
 * Money requests (solicitudes) listing — `GET /widget/links/:linkId/requests`.
 *
 * A "request" is the record of the user asking to be paid (composed in this
 * flow, which mints a fund-request link to share). It is distinct from an
 * operation (an actual money send): a request is fulfilled when the payer who
 * opens the shared link creates the sending operation. The `status` tells the
 * requester whether their request was accepted and funded.
 */

export type RequestStatus =
  | "pending"
  | "accepted"
  | "funded"
  | "failed"
  | "expired"
  | "cancelled";

export interface RequestCorridor {
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
}

export interface RequestSummary {
  requestId: string;
  status: RequestStatus;
  /** Amount requested, in the destination (receive) currency. */
  amount?: number;
  corridor: RequestCorridor;
  /** Shareable URL of the minted fund-request link (to re-share). */
  fundRequestUrl?: string;
  fundRequestLinkId?: string;
  /** Fulfilling operation id, set once a payer accepts. */
  operationId?: string;
  acceptedAt?: string;
  fundedAt?: string;
  expiresAt?: string;
  createdAt: string;
  /** Payer display name once someone accepted/funded; absent while pending. */
  payerName?: string;
  /**
   * Settled receive amount once a fulfilling operation exists.
   * Present on terminal rows that omit the live `operation` snapshot.
   */
  receiveAmount?: number;
  /** Live snapshot of the fulfilling operation (SSE / list enrichment). */
  operation?: RequestOperationSnapshot;
}

/** Slim operation fields on list/SSE payloads. */
export interface RequestOperationSnapshot {
  operationId: string;
  status: string;
  fundingMethod?: string;
  fundingStatus?: string;
  /** Settled receive amount when the list/SSE snapshot includes it. */
  receiveAmount?: number;
}

/** Operation fields embedded in a request detail (receipt screen). */
export interface RequestOperationDetail {
  operationId: string;
  status: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingMethod?: string;
  fundingStatus?: string;
  createdAt: string;
}

export interface RequestDetail {
  requestId: string;
  status: RequestStatus;
  amount?: number;
  corridor: RequestCorridor;
  operationId?: string;
  operation?: RequestOperationDetail | null;
  fundRequestUrl?: string;
  fundRequestLinkId?: string;
  expiresAt?: string;
  acceptedAt?: string;
  fundedAt?: string;
  createdAt: string;
  /** Payer display name once someone accepted/funded; absent while pending. */
  payerName?: string;
}

interface ListRequestsApiResponse {
  success: boolean;
  data: RequestSummary[];
}

interface RequestDetailApiResponse {
  success: boolean;
  data: RequestDetail;
}

/**
 * Lists every money request originated by the claimed link, most-recent first —
 * `GET /widget/links/:linkId/requests`.
 */
export const listRequests = async (
  linkId: string
): Promise<RequestSummary[]> => {
  const { data } = await widgetApi.get<ListRequestsApiResponse>(
    `/widget/links/${linkId}/requests`
  );
  return data.data;
};

/**
 * Fetches a single request with its fulfilling operation (when present) for
 * the requester's receipt screen — `GET /widget/links/:linkId/requests/:requestId`.
 */
export const getRequestDetail = async (
  linkId: string,
  requestId: string
): Promise<RequestDetail> => {
  const { data } = await widgetApi.get<RequestDetailApiResponse>(
    `/widget/links/${linkId}/requests/${requestId}`
  );
  return data.data;
};
